import { OpenImgContextProvider } from "openimg/react";
import {
  data,
  Links,
  LinksFunction,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLoaderData,
} from "react-router";
import { href as iconsHref } from "./components/ui/icon";
import tailwindStyleSheetUrl from "./styles/tailwind.css?url";
import globals from "@repo/ui/styles/globals.css?url";
import { useTranslation } from "react-i18next";
import { useChangeLanguage } from "remix-i18next/react";
import { Route } from "./+types/root";

import "@fontsource-variable/inter";
import "@fontsource-variable/lexend";
import interWoff2 from "@fontsource-variable/inter/files/inter-vietnamese-wght-normal.woff2?url";
import lexendWoff2 from "@fontsource-variable/lexend/files/lexend-vietnamese-wght-normal.woff2?url";
import { localeCookie } from "./localization/i18n";
import i18next from "./localization/i18n.server";
import { Language } from "./localization/resource";
import { getEnv } from "./utils/env.server";
import { useNonce } from "./utils/nonce-provider";
import { getImgSrc } from './utils/misc';
import { DynamicLinks } from './components/dynamic-links';
import { GlobalPendingUI } from './components/global-loading';
import { SeoStructuredScripts } from './components/seo-structured-scripts';
import { useLocation } from 'react-router';
import { useMemo } from "react";
import { menu_header } from './data/headerNavLinks';

export const links: LinksFunction = () => [
  { rel: "preload", href: iconsHref, as: "image" },

  { rel: "stylesheet", href: globals },
  { rel: "stylesheet", href: tailwindStyleSheetUrl },
  {
    rel: "icon",
    href: "/favicon.ico",
    sizes: "32x32",
  },
  {
    rel: "preload",
    href: interWoff2,
    as: "font",
    type: "font/woff2",
    crossOrigin: "anonymous",
  },
  {
    rel: "preload",
    href: lexendWoff2,
    as: "font",
    type: "font/woff2",
    crossOrigin: "anonymous",
  },
];
export const handle = {
  i18n: ["common"],
};
export async function loader({ request }: Route.LoaderArgs) {
  // Force default to 'vi' instead of browser language on first load
  const cookieLang = await localeCookie.parse(request.headers.get("Cookie"));
  const locale = (cookieLang || "vi") as Language;
  const t = await i18next.getFixedT(request, "common");
  const title = t("title");
  const origin = new URL(request.url).origin;

  return data(
    {
      locale,
      title,
      ENV: getEnv(),
      origin,
    },
    {
      headers: {
        "Set-Cookie": await localeCookie.serialize(locale),
        "Accept-Language": locale,
      },
    }
  );
}

export const meta = () => {
  const { title } = useLoaderData<typeof loader>();
  return [{ title: title }];
};

export function Layout({ children }: { children: React.ReactNode }) {
  const { i18n, t } = useTranslation("common");
  const data = useLoaderData<typeof loader>();
  const nonce = useNonce();
  const isProd = data?.ENV.MODE === "production";
  const location = useLocation();
  const isHomePage = location.pathname === "/";

  const breadcrumbFullUrl = useMemo(
    () => `${data.origin}${location.pathname}${location.search}`,
    [data.origin, location.pathname, location.search]
  );

  const breadcrumbNames = useMemo(() => {
    const path = location.pathname;
    const segments = path.split("/").filter(Boolean);
    if (!segments.length) return [];

    const matchedMenu = menu_header
      .filter((m) => m.href !== "/" && path.startsWith(m.href))
      .sort((a, b) => b.href.length - a.href.length)[0];

    const names: string[] = [];
    segments.forEach((seg, idx) => {
      if (idx === 0 && matchedMenu) {
        names.push(
          t(matchedMenu.title as string, {
            defaultValue: String(matchedMenu.title),
          })
        );
      } else {
        names.push(decodeURIComponent(seg).replace(/-/g, " "));
      }
    });
    return names;
  }, [location.pathname, t]);

  return (
    <html lang={i18n.language} dir={i18n.dir()}>
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        {isProd ? (
          <>
            <script
              async
              nonce={nonce}
              src="https://www.googletagmanager.com/gtag/js?id=G-ZK97GBYP7G"
            />
            <script
              nonce={nonce}
              dangerouslySetInnerHTML={{
                __html: [
                  "window.dataLayer = window.dataLayer || [];",
                  "function gtag(){dataLayer.push(arguments);}",
                  "gtag('js', new Date());",
                  "",
                  "gtag('config', 'G-ZK97GBYP7G');",
                ].join("\n"),
              }}
            />
          </>
        ) : null}
        <Meta />
        <DynamicLinks/>
        <Links />
        { !isHomePage ? (
          <SeoStructuredScripts
            nonce={nonce}
            breadcrumbFullUrl={breadcrumbFullUrl}
            breadcrumbNames={breadcrumbNames}
          />
        ) : null}
      </head>
      <body>
        {children}
        <GlobalPendingUI/>
        <ScrollRestoration />
        <script
          nonce={nonce}
          dangerouslySetInnerHTML={{
            __html: `window.ENV = ${JSON.stringify(data?.ENV)}`,
          }}
        />
        <Scripts />

      </body>
    </html>
  );
}
export default function App({ loaderData }: Route.ComponentProps) {
  const { locale } = loaderData;
  useChangeLanguage(locale);
  return (
    <OpenImgContextProvider
      optimizerEndpoint="/resources/images"
      getSrc={getImgSrc}
    >
      <Outlet />
    </OpenImgContextProvider>
  );
}
